// features/pos/services/posApi.ts
import apiClient from '@/lib/api-client';

// ============================================================
// TYPES
// ============================================================

export interface ProductResult {
    id: string;
    name: string;
    slug: string;
    retailPrice: string;
    wholesalePrice?: string;
    clearancePrice?: string;
    costPrice?: string;
    brand?: string;
    categoryName?: string;
    categoryId?: string;
    imageUrls?: string[];
    isClearance?: boolean;
    isPharmaceutical?: boolean;
    hasVariants?: boolean;
    barcode?: string;
    sku?: string;
    // Stock-related fields (enriched in POS page)
    isOutOfStock?: boolean;
    isLowStock?: boolean;
    availableQuantity?: string;
    isConfirmed?: boolean;
    uoms?: Array<{
        id: string;
        name: string;
        abbreviation: string;
        retailPrice?: string;
        isBaseUom: boolean;
    }>;
    variants?: Array<{
        id: string;
        name: string;
        retailPrice?: string;
        stockQuantity?: string;
    }>;
}

export interface BarcodeLookupResult {
    productId: string;
    productName: string;
    variantId?: string;
    variantName?: string;
    uomId: string;
    uomName: string;
    uomAbbreviation: string;
    retailPrice: string;
    wholesalePrice?: string;
    clearancePrice?: string;
    imageUrl?: string;
    availableQuantity: string;
    isConfirmed: boolean;
    confirmationStatus: string;
}

export interface StockConfirmationStatus {
    confirmed: boolean;
    availableQuantity: string;
    confirmationStatus: string;
    hasRecord?: boolean; // ✅ NEW: Whether a stock record exists
}

// ============================================================
// PRODUCT SEARCH
// ============================================================

export async function searchProducts(
    tenantId: string,
    search?: string,
    categoryId?: string,
    branchId?: string
): Promise<ProductResult[]> {
    const params: Record<string, unknown> = {
        limit: 50,  // ✅ Changed to 50 max products
        sortBy: 'name',
        order: 'asc',
        isActive: true,
        excludeRecipes: true, // Retail POS only sells retail goods, not kitchen recipes
    };
    if (search) params.search = search;
    if (categoryId) params.categoryId = categoryId;
    if (branchId && branchId !== '00000000-0000-0000-0000-000000000000') {
        params.branchId = branchId;
    }

    const { data } = await apiClient.get(`/tenants/${tenantId}/products`, { params });

    // ✅ Handle paginated response shape
    return Array.isArray(data?.data) ? data.data : (data?.data?.items || data?.data || []);
}

// ============================================================
// BARCODE LOOKUP (NEW)
// ============================================================

export async function lookupBarcode(
    tenantId: string,
    barcode: string,
    branchId?: string
): Promise<BarcodeLookupResult | null> {
    try {
        const params: Record<string, string> = {};
        if (branchId) params.branchId = branchId;

        const { data } = await apiClient.get(
            `/tenants/${tenantId}/barcode/${encodeURIComponent(barcode)}`,
            { params }
        );
        return data?.data || null;
    } catch {
        return null;
    }
}

export async function batchLookupBarcodes(
    tenantId: string,
    barcodes: string[],
    branchId?: string
): Promise<Record<string, BarcodeLookupResult>> {
    try {
        const params: Record<string, string> = {};
        if (branchId) params.branchId = branchId;

        const { data } = await apiClient.post(
            `/tenants/${tenantId}/barcode/batch`,
            { barcodes },
            { params }
        );
        return data?.data || {};
    } catch {
        return {};
    }
}

// ============================================================
// CATEGORIES
// ============================================================

export async function getCategories(tenantId: string): Promise<any[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/categories/tree`);
    return data?.data || [];
}

// ============================================================
// STOCK CONFIRMATION (UPDATED)
// ============================================================

export async function batchCheckStockConfirmation(
    tenantId: string,
    productIds: string[],
    branchId?: string
): Promise<Record<string, StockConfirmationStatus>> {
    try {
        const effectiveBranchId = branchId || '00000000-0000-0000-0000-000000000000';

        const params: Record<string, unknown> = {
            branchId: effectiveBranchId,
            limit: productIds.length + 10,
        };

        const { data } = await apiClient.get(`/tenants/${tenantId}/stock/levels`, { params });

        // ✅ FIX: Handle paginated response shape
        // buildPaginatedResponse returns: { success: true, data: [...items], meta: {...} }
        const stockLevels = Array.isArray(data?.data)
            ? data.data
            : (data?.data?.items || data?.items || []);

        const result: Record<string, StockConfirmationStatus> = {};

        // Initialize all products with UNAVAILABLE defaults
        for (const pid of productIds) {
            result[pid] = {
                confirmed: false,
                availableQuantity: '0',
                confirmationStatus: 'pending',
                hasRecord: false,
            };
        }

        // Update with actual stock data
        for (const level of stockLevels) {
            if (productIds.includes(level.productId)) {
                result[level.productId] = {
                    confirmed: level.confirmationStatus === 'confirmed',
                    availableQuantity: level.availableQuantity || level.quantityOnHand || '0',
                    confirmationStatus: level.confirmationStatus || 'pending',
                    hasRecord: true,
                };
            }
        }

        return result;
    } catch (error) {
        console.error('[batchCheckStockConfirmation] Error:', error);
        const result: Record<string, StockConfirmationStatus> = {};
        for (const pid of productIds) {
            result[pid] = {
                confirmed: false,
                availableQuantity: '0',
                confirmationStatus: 'pending',
                hasRecord: false,
            };
        }
        return result;
    }
}


// ============================================================
// SHIFTS
// ============================================================
export async function getActiveShift(tenantId: string, posType: 'retail' | 'bom' = 'retail'): Promise<any> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/shifts/active`, {
        params: { posType }
    });
    return data?.data || null;
}

export async function openShift(
    tenantId: string,
    branchId: string | undefined,
    openingCash: string,
    posType: 'retail' | 'bom' = 'retail'
): Promise<any> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/shifts/open`, {
        branchId,
        openingCash,
        posType,
    });
    return data?.data || data;
}

// ============================================================
// CART & CHECKOUT
// ============================================================

export async function parkCart(
    tenantId: string,
    cartData: {
        branchId: string;
        items: Array<{
            productId: string;
            variantId?: string;
            uomId: string;
            quantity: string;
            unitPrice: string;
            discountAmount: string;
        }>;
        customerId?: string;
        saleType: string;
        tableId?: string;
        posTerminalId?: string;  // ✅ ADD THIS
    }
): Promise<any> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/sales/cart`, cartData);
    return data?.data || data;
}

export async function checkout(
    tenantId: string,
    cartId: string,
    payments: Array<{
        paymentMethod: string;
        amount: number;
        reference?: string;
        bankAccountId?: string;
        mobileMoneyProvider?: string;
    }>,
    discountTotal: string
): Promise<any> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/sales/checkout`, {
        cartId,
        payments: payments.map((p) => ({
            paymentMethod: p.paymentMethod,
            amount: p.amount.toString(),
            reference: p.reference,
            bankAccountId: p.bankAccountId,
            mobileMoneyProvider: p.mobileMoneyProvider,
        })),
        discountTotal,
    });
    return data?.data || data;
}

export async function directCheckout(
    tenantId: string,
    saleData: {
        branchId: string;
        shiftId?: string;
        items: Array<{
            productId: string;
            variantId?: string;
            uomId?: string;
            quantity: string;
            unitPrice: string;
            discountAmount: string;
            notes?: string;
        }>;
        payments: Array<{
            paymentMethod: string;
            amount: number | string;
            reference?: string;
            bankAccountId?: string;
            mobileMoneyProvider?: string;
        }>;
        customerId?: string;
        saleType?: string;
        tableId?: string;
        posTerminalId?: string;
        notes?: string;
        discountTotal?: string;
        discountReason?: string;
        taxExempt?: boolean;
    }
): Promise<any> {
    const payload = {
        ...saleData,
        payments: saleData.payments.map((p) => {
            let method = p.paymentMethod;
            if (method === 'card') method = 'pos_card';
            return {
                paymentMethod: method,
                amount: p.amount.toString(),
                reference: p.reference,
                bankAccountId: p.bankAccountId,
                mobileMoneyProvider: p.mobileMoneyProvider,
            };
        }),
        discountTotal: saleData.discountTotal || '0',
    };
    const { data } = await apiClient.post(`/tenants/${tenantId}/sales/direct`, payload);
    return data?.data || data;
}

// ============================================================
// CUSTOMERS
// ============================================================

export async function findCustomerByPhone(
    tenantId: string,
    phone: string
): Promise<{
    id: string;
    firstName: string;
    lastName: string;
    phone: string;
    loyaltyCardNumber?: string;
    loyaltyPoints?: string;
    loyaltyTier?: string;
    storeCredit?: string;
    totalSpent?: string;
    visitCount?: string;
    debtBalance?: string;
} | null> {
    try {
        const { data } = await apiClient.get(`/tenants/${tenantId}/customers/phone/${phone}`);
        const customer = data?.data || data;
        if (!customer) return null;
        return {
            id: customer.id,
            firstName: customer.firstName,
            lastName: customer.lastName,
            phone: customer.phone,
            loyaltyCardNumber: customer.loyaltyCardNumber || '',
            loyaltyPoints: customer.loyaltyPoints || '0',
            loyaltyTier: customer.loyaltyTier || 'bronze',
            storeCredit: customer.storeCredit || '0',
            totalSpent: customer.totalSpent || '0',
            visitCount: customer.visitCount || '0',
            debtBalance: customer.debtBalance || '0',
        };
    } catch {
        return null;
    }
}

// ============================================================
// BRANCHES
// ============================================================

export async function getBranches(tenantId: string): Promise<any[]> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/branches`);
    return data?.data || [];
}


// Add these functions to posApi.ts

// ============================================================
// RECEIPT
// ============================================================

export async function getReceiptHtml(tenantId: string, saleId: string): Promise<string> {
    const { data } = await apiClient.get(
        `/tenants/${tenantId}/sales/${saleId}/receipt/html`,
        { responseType: 'text' }
    );
    return typeof data === 'string' ? data : data?.data || '';
}

export async function getReceiptData(tenantId: string, saleId: string): Promise<any> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/sales/${saleId}/receipt`);
    return data?.data || data;
}

// ============================================================
// SETTINGS (for tax rate)
// ============================================================

export async function getDefaultTax(tenantId: string): Promise<{ rate: number; name: string } | null> {
    try {
        const { data } = await apiClient.get(`/tenants/${tenantId}/taxes`);
        const taxes = data?.data || [];
        const defaultTax = taxes.find((t: any) => t.isDefault) || taxes[0];
        return defaultTax ? { rate: defaultTax.rate, name: defaultTax.name } : null;
    } catch {
        return null;
    }
}

// ============================================================
// NUMBER SEQUENCES
// ============================================================

export async function getNextOrderNumber(tenantId: string): Promise<string> {
    try {
        const { data } = await apiClient.post(`/tenants/${tenantId}/number-sequences/SALES_ORDER/next`);
        return data?.data?.nextNumber || '';
    } catch {
        // Fallback if number sequence not set up
        const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
        const random = Math.random().toString(36).slice(2, 7).toUpperCase();
        return `INV-${today}-${random}`;
    }
}

// ============================================================
// OPEN CASH DRAWER (ESC/POS command via print)
// ============================================================

export function openCashDrawer(): void {
    try {
        // ESC/POS command to open cash drawer (works with most thermal printers)
        // This sends the command to the default printer via a hidden iframe print
        const printWindow = window.open('', '_blank', 'width=1,height=1');
        if (printWindow) {
            printWindow.document.write('<html><body>');
            printWindow.document.write('<scr' + 'ipt>window.print();window.close();</scr' + 'ipt>');
            printWindow.document.write('</body></html>');
            printWindow.document.close();
        }
    } catch {
        // Silent fail - cash drawer is optional hardware
        console.log('Cash drawer trigger skipped (no printer available)');
    }
}

// ============================================================
// KDS (Kitchen Display System)
// ============================================================

export async function getKdsOrders(
    tenantId: string,
    branchId: string,
    status?: string
): Promise<any[]> {
    try {
        const params: Record<string, string> = {};
        if (status) params.status = status;
        const { data } = await apiClient.get(
            `/tenants/${tenantId}/branches/${branchId}/kds`,
            { params }
        );
        if (Array.isArray(data)) return data;
        if (data?.data && Array.isArray(data.data)) return data.data;
        return [];
    } catch (error) {
        console.error('[posApi] getKdsOrders error:', error);
        return [];
    }
}

export async function updateKdsStatus(
    tenantId: string,
    kdsId: string,
    status: string
): Promise<void> {
    await apiClient.patch(
        `/tenants/${tenantId}/kds/${kdsId}/status`,
        { status }
    );
}

// ============================================================
// RECEIPT VERIFICATION (EXIT SCANNING)
// ============================================================

export async function verifyReceipt(
    tenantId: string,
    saleId?: string,
    orderNumber?: string
): Promise<{
    status: 'valid' | 'already_used' | 'not_found';
    sale?: any;
    message: string;
}> {
    const { data } = await apiClient.post(`/tenants/${tenantId}/receipt/verify`, {
        saleId,
        orderNumber,
    });
    return data?.data || data;
}

export async function checkReceiptStatus(
    tenantId: string,
    saleId: string
): Promise<{
    exists: boolean;
    isScanned: boolean;
    scannedAt?: string;
    scannedBy?: string;
}> {
    const { data } = await apiClient.get(`/tenants/${tenantId}/receipt/verify/${saleId}`);
    return data?.data || data;
}

// ============================================================
// BANK ACCOUNTS (for POS Payment Modal)
// ============================================================

export async function getBankAccountsForBranch(
    tenantId: string,
    branchId?: string
): Promise<Array<{
    id: string;
    bankName: string;
    accountNumber: string;
    accountName: string;
    branchId: string | null;
    isActive: boolean;
    currentBalance: string;
}>> {
    try {
        const params: Record<string, string> = {};
        if (branchId) params.branchId = branchId;

        const { data } = await apiClient.get(`/tenants/${tenantId}/bank-accounts`, { params });
        const accounts = data?.data || [];

        // Filter: branch accounts first, then business accounts (branchId null), fallback to all active accounts
        if (branchId) {
            const branchAccounts = accounts.filter((a: any) => a.branchId === branchId && a.isActive);
            const businessAccounts = accounts.filter((a: any) => !a.branchId && a.isActive);
            const combined = [...branchAccounts, ...businessAccounts];
            if (combined.length > 0) return combined;
        }

        return accounts.filter((a: any) => a.isActive);
    } catch {
        return [];
    }
}

// ============================================================
// POS TERMINALS
// ============================================================

export async function getPosTerminals(tenantId: string): Promise<any[]> {
    try {
        const { data } = await apiClient.get(`/tenants/${tenantId}/pos-terminals`);
        return data?.data || [];
    } catch {
        return [];
    }
}

export async function getPosTerminalById(tenantId: string, terminalId: string): Promise<any | null> {
    try {
        // Get all terminals and find the one we need
        const terminals = await getPosTerminals(tenantId);
        return terminals.find((t: any) => t.id === terminalId) || null;
    } catch {
        return null;
    }
}

// ============================================================
// PAYMENT METHODS (from Settings)
// ============================================================

export async function getPaymentMethods(tenantId: string): Promise<Array<{
    id: string; name: string; code: string; type: string;
    isActive: boolean; requiresReference: boolean; settings: Record<string, unknown>;
}>> {
    try {
        const { data } = await apiClient.get(`/tenants/${tenantId}/payment-methods`);
        return (data?.data || []).filter((m: any) => m.isActive);
    } catch {
        // Fallback defaults
        return [
            { id: 'cash', name: 'Cash', code: 'CASH', type: 'cash', isActive: true, requiresReference: false, settings: {} },
            { id: 'transfer', name: 'Bank Transfer', code: 'TRANSFER', type: 'bank_transfer', isActive: true, requiresReference: true, settings: {} },
            { id: 'card', name: 'Card', code: 'CARD', type: 'card', isActive: true, requiresReference: false, settings: {} },
            { id: 'momo', name: 'Mobile Money', code: 'MOMO', type: 'mobile_money', isActive: true, requiresReference: false, settings: { providers: ['Opay', 'PalmPay', 'Paga', 'MTN Momo', 'Airtel Money'] } },
        ];
    }
}

// ============================================================
// DISCOUNT REASONS (from Settings)
// ============================================================

export async function getDiscountReasons(tenantId: string): Promise<Array<{
    id: string; name: string; type: 'percentage' | 'fixed';
    maxAmount: number | null; requiresApproval: boolean; isActive: boolean;
}>> {
    try {
        const { data } = await apiClient.get(`/tenants/${tenantId}/discount-reasons`);
        const reasons = data?.data || [];
        return reasons.filter((r: any) => r.isActive);
    } catch {
        // Fallback defaults
        return [
            { id: 'happy_hour', name: 'Happy Hour 20%', type: 'percentage', maxAmount: 20, requiresApproval: false, isActive: true },
            { id: 'employee', name: 'Employee 30%', type: 'percentage', maxAmount: 30, requiresApproval: true, isActive: true },
            { id: 'senior', name: 'Senior Citizen 10%', type: 'percentage', maxAmount: 10, requiresApproval: false, isActive: true },
            { id: 'clearance', name: 'Clearance 50%', type: 'percentage', maxAmount: 50, requiresApproval: false, isActive: true },
            { id: 'fixed_500', name: '₦500 Off', type: 'fixed', maxAmount: 500, requiresApproval: false, isActive: true },
            { id: 'fixed_200', name: '₦200 Off', type: 'fixed', maxAmount: 200, requiresApproval: false, isActive: true },
        ];
    }
}

// ============================================================
// RECENT CUSTOMERS (for quick selection)
// ============================================================

export async function getRecentCustomers(tenantId: string, limit: number = 5): Promise<any[]> {
    try {
        const { data } = await apiClient.get(`/tenants/${tenantId}/customers`, { params: { limit, sortBy: 'updatedAt', order: 'desc' } });
        return data?.data || [];
    } catch {
        return [];
    }
}